import { useCallback } from 'react';

import { useHistory, useLocation } from 'react-router-dom';

import { setSignInLandingPage } from 'apps/customer/state/route';

import { appendSearchParams } from './appendSearchParams';
import {
  setIsFlowComplete,
  setIsReturningUser,
  setPendingSubscriptionResume,
} from './onboardingSession';
import { OnboardingStep } from './OnboardingStep';
import { PATH_FOR_STEP } from './stepRoutes';

import type { FlowNextAction } from './flowResolution';

export function useSignInOnboardingNavigation() {
  const history = useHistory();
  const location = useLocation();

  const finish = useCallback(
    (
      path: string,
      {
        preserveSearch = true,
        replace = false,
      }: { preserveSearch?: boolean; replace?: boolean } = {},
    ) => {
      setIsReturningUser(null);
      setIsFlowComplete(false);
      setPendingSubscriptionResume(null);
      setSignInLandingPage(null);
      const destination = preserveSearch
        ? appendSearchParams(path, location.search)
        : path.split('?')[0];
      if (replace) history.replace(destination);
      else history.push(destination);
    },
    [history, location.search],
  );

  const navigate = useCallback(
    (action: FlowNextAction) => {
      if (action.type === 'step') {
        const destination = appendSearchParams(PATH_FOR_STEP[action.step], location.search);
        if (action.replace) history.replace(destination);
        else history.push(destination);
        return;
      }
      if (action.type === 'external') {
        if (action.resumeOnboardingAfterTasks) {
          setIsFlowComplete(false);
          setPendingSubscriptionResume({
            search: location.search,
            variant: action.resumeOnboardingAfterTasks,
          });
          history.push(appendSearchParams(action.path, location.search));
          return;
        }
        finish(action.path);
        return;
      }
      setIsFlowComplete(true);
      history.replace(appendSearchParams(PATH_FOR_STEP[OnboardingStep.Success], location.search));
    },
    [finish, history, location.search],
  );

  return { finish, navigate };
}
